import { getProjectDetailsById, getProjects } from '@/api/projects.api';
import { useProjectContext } from '@/context/ProjectContext';
import type { Project } from '@/interfaces/projects.interface';
import { useEffect, useRef } from 'react';

interface UseEnsureSelectedProjectInListParams {
  /** Current page of options shown by the dropdown. */
  items: Project[];
  /** Items pulled from the API are merged into the dropdown via this callback. */
  mergeProjects: (extra: Project[]) => void;
  /** Page size used when searching the selected project by name. */
  pageSize: number;
}

/**
 * Guarantees that the currently selected project (`selectedProjectId` from
 * `ProjectContext`) appears in the dropdown's `items` list, even when it sits
 * on a later page or has been removed from the cached page-1 results.
 *
 * Strategy:
 * 1. If the selected project is already in `items`, do nothing.
 * 2. If the cached selected project (from context) matches, merge it locally.
 * 3. Otherwise fetch it by name (cheap), then by id (authoritative) and merge.
 *
 * A request-generation counter cancels stale responses when the user switches
 * projects quickly, so a slow earlier fetch can never overwrite a newer one.
 */
export function useEnsureSelectedProjectInList({
  items,
  mergeProjects,
  pageSize,
}: UseEnsureSelectedProjectInListParams): void {
  const {
    selectedProjectId,
    selectedProjectName,
    cachedSelectedProject,
    setCachedSelectedProject,
  } = useProjectContext();

  // Refs so the effect doesn't re-run when items/cachedSelectedProject change;
  // it only needs to act on selection changes.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const cachedSelectedRef = useRef(cachedSelectedProject);
  cachedSelectedRef.current = cachedSelectedProject;

  const requestGenRef = useRef(0);
  const completedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    completedKeyRef.current = null;
  }, [selectedProjectId, selectedProjectName]);

  useEffect(() => {
    if (!selectedProjectId || !selectedProjectName?.trim()) return;
    if (itemsRef.current.some((p) => p.projectId === selectedProjectId)) return;

    const key = `${selectedProjectId}:${selectedProjectName.trim()}`;
    if (completedKeyRef.current === key) return;

    const cached = cachedSelectedRef.current;
    if (cached?.projectId === selectedProjectId && cached.projectName?.trim()) {
      mergeProjects([cached]);
      completedKeyRef.current = key;
      return;
    }

    const requestId = requestGenRef.current;
    void (async () => {
      try {
        const response = await getProjects({
          page: 1,
          size: pageSize,
          search: selectedProjectName.trim(),
        });
        if (requestId !== requestGenRef.current) return;
        const pageItems = Array.isArray(response.items) ? response.items : [];
        mergeProjects(pageItems);
        if (!pageItems.some((p) => p.projectId === selectedProjectId)) {
          const res = await getProjectDetailsById(selectedProjectId);
          if (requestId !== requestGenRef.current) return;
          if (res.status === 200 && res.data) {
            mergeProjects([res.data]);
            setCachedSelectedProject(res.data);
          }
        }
      } finally {
        if (requestId === requestGenRef.current) {
          completedKeyRef.current = key;
        }
      }
    })();

    return () => {
      requestGenRef.current += 1;
    };
  }, [
    selectedProjectId,
    selectedProjectName,
    mergeProjects,
    pageSize,
    setCachedSelectedProject,
  ]);
}
